// "Tap a point on the grid" and "shade the parts" questions (15 Oct 2026).
// Both are answered on the picture, and both are sent the way every list
// answer is: as strings that the marker compares with the answer key as a
// set. So an answer and its key must be written the same way, and the
// functions here are the one place that decides how.
//
// - A tapped grid point is "pt=<x>,<y>", whole numbers with "-" for a
//   negative ("pt=3,-2"). A question may ask for several points; there is
//   one entry for each.
// - Shading is marked by HOW MANY parts are shaded, never which ones:
//   "shaded=<count>".
//
// Neither can be mistaken for a several-boxes or sort entry, whose id before
// the "=" is always a single letter. That is why adult pages may tell these
// two apart by the text alone (formatAnswer.ts).

export type GridPoint = { x: number; y: number };

export function gridPointEntry(point: GridPoint): string {
  return `pt=${String(point.x)},${String(point.y)}`;
}

export function parseGridPointEntry(entry: unknown): GridPoint | null {
  if (typeof entry !== 'string') return null;
  const match = /^pt=(-?\d{1,4}),(-?\d{1,4})$/.exec(entry);
  if (!match) return null;
  return { x: Number(match[1]), y: Number(match[2]) };
}

// The tapped points as they are kept while a student works: one "x,y" on
// each line, in the order they were tapped.
export function splitGridPoints(text: string): GridPoint[] {
  return text
    .split('\n')
    .map((line) => parseGridPointEntry(`pt=${line}`))
    .filter((point): point is GridPoint => point !== null);
}

export function joinGridPoints(points: GridPoint[]): string {
  return points
    .map((point) => `${String(point.x)},${String(point.y)}`)
    .join('\n');
}

// Tapping a point that is already chosen clears it.
export function toggleGridPoint(
  points: GridPoint[],
  point: GridPoint,
): GridPoint[] {
  const without = points.filter((p) => p.x !== point.x || p.y !== point.y);
  return without.length === points.length ? [...points, point] : without;
}

// What is sent, in one agreed order so the same points always read the same.
export function gridPointOrder(points: GridPoint[]): string[] {
  return points.map(gridPointEntry).sort();
}

type GridBounds = { xMin: number; xMax: number; yMin: number; yMax: number };

// An answer key from the question bank: a list of [x, y] pairs. Null unless
// every pair is a crossing of that grid and no pair is given twice.
export function gridPointKey(
  answer: unknown,
  grid: GridBounds,
): string[] | null {
  if (!Array.isArray(answer) || answer.length === 0) return null;
  const points: GridPoint[] = [];
  for (const pair of answer) {
    if (!Array.isArray(pair) || pair.length !== 2) return null;
    const [x, y] = pair as unknown[];
    if (
      typeof x !== 'number' ||
      typeof y !== 'number' ||
      !Number.isInteger(x) ||
      !Number.isInteger(y) ||
      x < grid.xMin ||
      x > grid.xMax ||
      y < grid.yMin ||
      y > grid.yMax
    )
      return null;
    points.push({ x, y });
  }
  const order = gridPointOrder(points);
  return new Set(order).size === order.length ? order : null;
}

export function shadedEntry(count: number): string {
  return `shaded=${String(count)}`;
}

export function parseShadedEntry(entry: unknown): number | null {
  if (typeof entry !== 'string') return null;
  const match = /^shaded=(\d{1,4})$/.exec(entry);
  return match ? Number(match[1]) : null;
}

// The shaded parts as they are kept while a student works: the place of
// each shaded part, counted from 0, one on each line.
export function splitShaded(text: string, parts: number): number[] {
  const seen = new Set<number>();
  for (const line of text.split('\n')) {
    if (!/^\d{1,4}$/.test(line)) continue;
    const index = Number(line);
    if (index < parts) seen.add(index);
  }
  return [...seen].sort((a, b) => a - b);
}

export function toggleShaded(shaded: number[], index: number): number[] {
  return shaded.includes(index)
    ? shaded.filter((other) => other !== index)
    : [...shaded, index].sort((a, b) => a - b);
}

// An answer key from the question bank: how many parts to shade. Null
// unless it is a whole number from 1 to the number of parts.
export function shadedKey(answer: unknown, parts: number): string[] | null {
  return typeof answer === 'number' &&
    Number.isInteger(answer) &&
    answer >= 1 &&
    answer <= parts
    ? [shadedEntry(answer)]
    : null;
}
